import { useEffect, useRef, useState } from "react";

type Fee = {
  label: string;
  short: string;
  cost: number;
  cum: number;
};

const FEES: Fee[] = [
  { label: "DEPOSIT 50%", short: "DEPOSIT", cost: 3500, cum: 3500 },
  { label: "KICKOFF MEETING", short: "KICKOFF", cost: 4200, cum: 7700 },
  { label: "DISCOVERY WORKSHOP", short: "DISCOVERY", cost: 5500, cum: 13200 },
  { label: "BRAND STRATEGY DECK", short: "BRAND STRAT.", cost: 6000, cum: 19200 },
  { label: "WIREFRAMES REVIEW", short: "WIREFRAMES", cost: 1500, cum: 20700 },
  { label: "REVISION ROUND 1", short: "REVISION ×1", cost: 200, cum: 20900 },
  { label: "PROJECT MGMT FEE", short: "PROJ. MGMT", cost: 300, cum: 21200 },
  { label: "REVISION ROUND 2", short: "REVISION ×2", cost: 200, cum: 21400 },
  { label: "FINAL BALANCE 50%", short: "FINAL BAL.", cost: 6750, cum: 28150 },
  { label: "POST-LAUNCH FIXES", short: "POST-LAUNCH", cost: 300, cum: 28450 },
  { label: "RETAINER BEGINS", short: "RETAINER", cost: 400, cum: 28850 },
];

// Red-blip positions — clustered, lively
const SLOTS: { angle: number; dist: number }[] = [
  { angle: 0.28, dist: 0.72 },
  { angle: 0.85, dist: 0.55 },
  { angle: 1.42, dist: 0.78 },
  { angle: 1.99, dist: 0.52 },
  { angle: 2.56, dist: 0.75 },
  { angle: 3.13, dist: 0.58 },
  { angle: 3.7, dist: 0.8 },
  { angle: 4.27, dist: 0.54 },
  { angle: 4.84, dist: 0.7 },
  { angle: 5.41, dist: 0.6 },
  { angle: 5.98, dist: 0.76 },
];

// Final skill ring — evenly spread, avoids the central text band
const SKILL_POSITIONS: { angle: number; dist: number }[] = [
  { angle: -Math.PI / 2 - 0.15, dist: 0.78 },     // top-left
  { angle: -Math.PI / 2 + 0.55, dist: 0.78 },     // top-right
  { angle: 0.35, dist: 0.85 },                    // right
  { angle: Math.PI / 2 - 0.55, dist: 0.78 },      // bottom-right
  { angle: Math.PI / 2 + 0.15, dist: 0.78 },      // bottom-left
  { angle: Math.PI - 0.35, dist: 0.85 },          // left
  { angle: -0.5, dist: 0.55 },                    // mid upper-right (filler)
];



type Skill = { name: string; lvl: number; xp: number };

const SKILLS: Skill[] = [
  { name: "PREDICTION MARKETS", lvl: 99, xp: 0.74 },
  { name: "CRYPTO", lvl: 99, xp: 0.61 },
  { name: "AGENTIC AI", lvl: 99, xp: 0.88 },
  { name: "AI INFLUENCERS", lvl: 99, xp: 0.69 },
  { name: "BRANDING", lvl: 99, xp: 0.82 },
  { name: "MENTORSHIP", lvl: 99, xp: 0.58 },
  { name: "SOCIAL MEDIA", lvl: 99, xp: 0.55 },
];

const WINS = [
  "WIX SITES KILLED · 47",
  "UBERS SAVED · 312",
  "DISCOVERY WORKSHOPS AVOIDED · 89",
  "PÁLINKA SHOTS · 14",
  "REVISION ROUNDS REFUSED · ∞",
  "BRAND DECKS NOT WRITTEN · 203",
  "MORNINGS TO LIVE · 1",
  "SYNC CALLS DECLINED · 671",
];

type Blip = {
  fee: Fee;
  cx: number;
  cy: number;
  r: number;
  born: number;
  state: "incoming" | "settled" | "neutralizing" | "skill" | "gone";
  alpha: number;
  skill?: Skill;
  morphT?: number;
  targetX?: number;
  targetY?: number;
};

export function Hero() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [phase, setPhase] = useState<"idle" | "running" | "neutralized">("idle");
  const [activeFee, setActiveFee] = useState<Fee | null>(null);
  const [burn, setBurn] = useState(0);
  const mouseRef = useRef({ x: 0.5, y: 0.5, active: false });

  // Cursor reactivity
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      mouseRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        active: true,
      };
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  // Sequence — fees burn in over ~22s, then neutralize
  useEffect(() => {
    setPhase("running");
    const blips: Blip[] = [];
    const start = performance.now();
    let raf = 0;
    let neutralizeAt = 0;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    // Schedule fees
    const feeTimers: number[] = [];
    FEES.forEach((fee, i) => {
      const t = window.setTimeout(() => {
        const slot = SLOTS[i % SLOTS.length];
        const w = canvas.offsetWidth;
        const h = canvas.offsetHeight;
        const cx = w / 2 + Math.cos(slot.angle) * slot.dist * Math.min(w, h) * 0.4;
        const cy = h / 2 + Math.sin(slot.angle) * slot.dist * Math.min(w, h) * 0.4;
        const r =
          fee.cost >= 5000 ? 9 : fee.cost >= 3000 ? 7 : fee.cost >= 1000 ? 5.5 : 4;
        blips.push({
          fee,
          cx,
          cy,
          r,
          born: performance.now(),
          state: "incoming",
          alpha: 0,
        });
        setActiveFee(fee);
        setBurn(fee.cum);
      }, 900 + i * 1700);
      feeTimers.push(t);
    });

    // Neutralize — selected blips morph into skill nodes and glide to ring positions; rest fade out
    neutralizeAt = window.setTimeout(() => {
      const morphIndices = [0, 2, 4, 5, 7, 8, 10];
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      const minDim = Math.min(w, h);
      blips.forEach((b, i) => {
        const skillIdx = morphIndices.indexOf(i);
        if (skillIdx !== -1 && skillIdx < SKILLS.length) {
          const pos = SKILL_POSITIONS[skillIdx];
          b.state = "skill";
          b.skill = SKILLS[skillIdx];
          b.morphT = performance.now();
          b.targetX = w / 2 + Math.cos(pos.angle) * pos.dist * minDim * 0.4;
          b.targetY = h / 2 + Math.sin(pos.angle) * pos.dist * minDim * 0.4;
        } else {
          b.state = "neutralizing";
        }
      });
      setActiveFee(null);
      window.setTimeout(() => setPhase("neutralized"), 1400);
    }, 900 + FEES.length * 1700 + 1200);

    // Render loop
    const render = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      const t = (performance.now() - start) / 1000;
      ctx.clearRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2;
      const maxR = Math.min(w, h) * 0.42;

      // Concentric rings — react to cursor with subtle parallax
      const mx = (mouseRef.current.x - 0.5) * 18;
      const my = (mouseRef.current.y - 0.5) * 18;

      ctx.save();
      ctx.translate(mx * 0.3, my * 0.3);
      for (let i = 1; i <= 6; i++) {
        ctx.beginPath();
        ctx.arc(cx, cy, (maxR / 6) * i, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(200, 134, 10, ${0.05 + (i === 6 ? 0.12 : 0.02)})`;
        ctx.lineWidth = i === 6 ? 1 : 0.5;
        ctx.stroke();
      }
      // Crosshairs
      ctx.beginPath();
      ctx.moveTo(cx - maxR, cy);
      ctx.lineTo(cx + maxR, cy);
      ctx.moveTo(cx, cy - maxR);
      ctx.lineTo(cx, cy + maxR);
      ctx.strokeStyle = "rgba(200, 134, 10, 0.08)";
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // Sweep
      const sweepAngle = (t * 0.7) % (Math.PI * 2);
      const grad = ctx.createConicGradient
        ? ctx.createConicGradient(sweepAngle, cx, cy)
        : null;
      if (grad) {
        grad.addColorStop(0, "rgba(240, 168, 32, 0.35)");
        grad.addColorStop(0.06, "rgba(240, 168, 32, 0.08)");
        grad.addColorStop(0.2, "rgba(240, 168, 32, 0)");
        grad.addColorStop(1, "rgba(240, 168, 32, 0)");
        ctx.beginPath();
        ctx.arc(cx, cy, maxR, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.globalCompositeOperation = "lighter";
        ctx.fill();
        ctx.globalCompositeOperation = "source-over";
      }

      // Cursor halo
      if (mouseRef.current.active) {
        const px = mouseRef.current.x * w;
        const py = mouseRef.current.y * h;
        const halo = ctx.createRadialGradient(px, py, 0, px, py, 160);
        halo.addColorStop(0, "rgba(240, 168, 32, 0.16)");
        halo.addColorStop(1, "rgba(240, 168, 32, 0)");
        ctx.fillStyle = halo;
        ctx.fillRect(0, 0, w, h);
      }

      ctx.restore();

      // Blips
      const now = performance.now();
      blips.forEach((b) => {
        const age = (now - b.born) / 1000;
        if (b.state === "incoming") {
          b.alpha = Math.min(1, age * 2.5);
          if (age > 0.4) b.state = "settled";
        }
        if (b.state === "neutralizing") {
          b.cx += (cx - b.cx) * 0.06;
          b.cy += (cy - b.cy) * 0.06;
          b.alpha *= 0.96;
          if (b.alpha < 0.02) b.state = "gone";
        }
        if (b.alpha <= 0) return;

        if (b.state === "skill" && b.skill) {
          // Glide toward final ring position
          if (b.targetX !== undefined && b.targetY !== undefined) {
            b.cx += (b.targetX - b.cx) * 0.08;
            b.cy += (b.targetY - b.cy) * 0.08;
          }
          // Morph progress 0..1 over 600ms
          const mt = Math.min(1, ((now - (b.morphT ?? now)) / 600));
          // Color blend red -> green
          const rR = 220 + (90 - 220) * mt;
          const rG = 80 + (220 - 80) * mt;
          const rB = 80 + (140 - 80) * mt;
          const ringR = b.r + 2 + Math.sin(t * 3 + b.born) * 1.4 + mt * 6;

          // Outer pulse halo
          ctx.beginPath();
          ctx.arc(b.cx, b.cy, ringR + 8, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${rR | 0}, ${rG | 0}, ${rB | 0}, ${0.18 * b.alpha})`;
          ctx.fill();

          // Inner pulse
          ctx.beginPath();
          ctx.arc(b.cx, b.cy, ringR + 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${rR | 0}, ${rG | 0}, ${rB | 0}, ${0.28 * b.alpha})`;
          ctx.fill();

          // Core dot
          ctx.beginPath();
          ctx.arc(b.cx, b.cy, b.r + 1, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${rR | 0}, ${rG | 0}, ${rB | 0}, ${b.alpha})`;
          ctx.fill();

          if (mt > 0.3) {
            const lblAlpha = ((mt - 0.3) / 0.7) * b.alpha;
            // Skill name above
            ctx.font = "bold 11px 'Share Tech Mono', monospace";
            ctx.textAlign = "center";
            ctx.fillStyle = `rgba(230, 240, 210, ${lblAlpha})`;
            ctx.fillText(b.skill.name, b.cx, b.cy - b.r - 10);
            ctx.textAlign = "left";
          }
          return;
        }

        // Default red blip (incoming/settled/neutralizing)
        ctx.beginPath();
        ctx.arc(b.cx, b.cy, b.r + Math.sin(t * 4 + b.born) * 1.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200, 64, 64, ${0.18 * b.alpha})`;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(b.cx, b.cy, b.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(220, 80, 80, ${0.9 * b.alpha})`;
        ctx.fill();

        ctx.font = "9px 'Share Tech Mono', monospace";
        ctx.fillStyle = `rgba(200, 134, 10, ${0.7 * b.alpha})`;
        ctx.fillText(b.fee.short, b.cx + b.r + 8, b.cy - 4);
        ctx.fillStyle = `rgba(220, 80, 80, ${0.85 * b.alpha})`;
        ctx.fillText(`-$${b.fee.cost.toLocaleString()}`, b.cx + b.r + 8, b.cy + 8);
      });

      // Center pulse — represents OMNIFIC
      const pulse = 4 + Math.sin(t * 2.5) * 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, pulse + 6, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(240, 168, 32, 0.12)";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, pulse, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(240, 168, 32, 0.95)";
      ctx.fill();

      raf = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      feeTimers.forEach(clearTimeout);
      clearTimeout(neutralizeAt);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-screen w-full overflow-hidden pt-11"
    >
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

      {/* Vignette */}
      <div
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background:
            "radial-gradient(ellipse 60% 60% at 50% 50%, transparent 45%, var(--background) 100%)",
        }}
      />

      {/* Scanlines */}
      <div className="scanlines" />

      {/* HUD top bar removed per request */}

      {/* Fee reveal — center */}
      <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center text-center">
        {phase === "running" && (
          <div className="px-6">
            <h2 className="font-display mx-auto max-w-[900px] text-center text-[clamp(38px,6vw,84px)] leading-[0.95] tracking-tight text-bone">
              THE COST OF{" "}
              <span className="text-gradient-amber italic not-italic">BUREAUCRACY</span>
            </h2>
            {activeFee && (
              <div
                key={activeFee.label}
                className="hero-fade-in mt-8 flex flex-col items-center gap-2 text-[10px] tracking-[0.32em]"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[var(--amber)]">▸ {activeFee.short}</span>
                  <span className="text-[var(--blood)] font-mono-tech">
                    −${activeFee.cost.toLocaleString()}
                  </span>
                </div>
                <span className="text-[var(--blood)] font-mono-tech">
                  TOTAL BURNED ${burn.toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}

        {phase === "neutralized" && (
          <div className="hero-fade-up px-6">
            <div className="font-display text-[clamp(38px,5vw,68px)] leading-none tracking-wide text-[oklch(0.6_0.21_25/0.42)] line-through decoration-[oklch(0.6_0.21_25/0.7)] decoration-[3px]">
              $28,850
            </div>
            <div className="mt-4 flex items-center justify-center gap-3 text-[9px] tracking-[0.36em] text-[var(--amber)]">
              <span className="h-px w-5 bg-[var(--amber)]/60" />
              STARTING AT
              <span className="h-px w-5 bg-[var(--amber)]/60" />
            </div>
            <div className="mt-2 font-display text-[clamp(78px,11vw,148px)] leading-[0.85] tracking-tight text-gradient-amber">
              $1,888
            </div>
            <p className="font-body mx-auto mt-6 max-w-[480px] text-[15px] font-light leading-[1.75] text-[var(--dim-2)]">
              <span className="font-medium text-bone">~4 weeks saved.</span>{" "}
              Concept to live in 12 hours.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 text-[9px] tracking-[0.28em] text-[var(--amber-bright)]">
              <span className="h-px w-6 bg-[var(--amber)]" />
              SCROLL TO ENGAGE
              <span className="h-px w-6 bg-[var(--amber)]" />
            </div>
          </div>
        )}
      </div>

      {/* /WINS ticker hidden for now */}
      {/* <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-20 hairline-t bg-[var(--background)]/90 backdrop-blur">
        <div className="flex items-stretch overflow-hidden text-[10px] tracking-[0.22em]">
          <div className="flex items-center gap-2 whitespace-nowrap border-r border-[var(--hairline)] px-5 py-3 text-[var(--signal)]">
            <span className="live-dot inline-block h-1.5 w-1.5 rounded-full bg-[var(--signal)]" />
            /WINS
          </div>
          <div className="relative flex-1 overflow-hidden py-3">
            <div className="animate-ticker flex whitespace-nowrap">
              {[...WINS, ...WINS, ...WINS].map((w, i) => (
                <span key={i} className="mx-8 text-[var(--dim-2)]">
                  <span className="mr-2 text-[var(--signal)]">▸</span>
                  {w}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div> */}
    </section>
  );
}
